import { z } from 'zod';
import { RichSlot, TextSlot, useProp } from '@docgen/sdk';
import type { ElementType } from 'react';
import type { BlockDef } from '../types.js';

export const eyebrowBlock: BlockDef = {
  type: 'eyebrow',
  name: 'Eyebrow',
  description: 'Small uppercase kicker line above a heading.',
  category: 'text',
  icon: 'case-upper',
  keepWithNext: true,
  propsSchema: z.object({ text: z.string() }),
  propFields: [{ kind: 'text', name: 'text', label: 'Text', default: 'Eyebrow' }],
  styleFields: ['color', 'mt', 'mb'],
  defaults: { props: { text: 'Eyebrow' } },
  Component: function Eyebrow({ attrs, style }) {
    return (
      <div {...attrs}>
        <TextSlot name="text" as="p" className="eyebrow" style={style} />
      </div>
    );
  },
};

const HEADING_TAGS: Record<string, ElementType> = { '1': 'h1', '2': 'h2', '3': 'h3' };

export const headingBlock: BlockDef = {
  type: 'heading',
  name: 'Heading',
  description: 'Page or section heading (levels 1–3).',
  category: 'text',
  icon: 'heading-1',
  keepWithNext: true,
  propsSchema: z.object({ text: z.string(), level: z.enum(['1', '2', '3']).default('2') }),
  propFields: [
    { kind: 'text', name: 'text', label: 'Text', default: 'Heading' },
    { kind: 'select', name: 'level', label: 'Level', options: ['1', '2', '3'], default: '2' },
  ],
  styleFields: ['color', 'fontSize', 'align', 'mt', 'mb'],
  defaults: { props: { text: 'Heading', level: '2' } },
  Component: function Heading({ attrs, style }) {
    const level = String(useProp('level') ?? '2');
    const Tag = HEADING_TAGS[level] ?? 'h2';
    return (
      <div {...attrs}>
        <TextSlot name="text" as={Tag} style={style} />
      </div>
    );
  },
};

export const paragraphBlock: BlockDef = {
  type: 'paragraph',
  name: 'Paragraph',
  description: 'A plain paragraph of body text.',
  category: 'text',
  icon: 'text',
  propsSchema: z.object({
    text: z.string(),
    size: z.enum(['base', 'small', 'fine']).default('base'),
  }),
  propFields: [
    { kind: 'text', name: 'text', label: 'Text', default: 'Paragraph text' },
    { kind: 'select', name: 'size', label: 'Size', options: ['base', 'small', 'fine'], default: 'base' },
  ],
  styleFields: ['color', 'align', 'mt', 'mb'],
  defaults: { props: { text: 'Paragraph text', size: 'base' } },
  Component: function Paragraph({ attrs, style }) {
    const size = useProp('size');
    const cls = size === 'small' ? 'txt-sm' : size === 'fine' ? 'txt-fine' : '';
    return (
      <div {...attrs}>
        <TextSlot name="text" as="p" className={cls} style={style} />
      </div>
    );
  },
};

export const sectionHeaderBlock: BlockDef = {
  type: 'sectionHeader',
  name: 'Section header',
  description: 'Eyebrow and heading with the bordered section divider.',
  category: 'text',
  icon: 'heading-2',
  keepWithNext: true,
  propsSchema: z.object({ eyebrow: z.string(), heading: z.string() }),
  propFields: [
    { kind: 'text', name: 'eyebrow', label: 'Eyebrow', default: 'Section' },
    { kind: 'text', name: 'heading', label: 'Heading', default: 'Section heading' },
  ],
  styleFields: ['color', 'mt', 'mb'],
  defaults: { props: { eyebrow: 'Section', heading: 'Section heading' } },
  Component: function SectionHeader({ attrs, style }) {
    return (
      <div {...attrs} className="sec-head" style={style}>
        <TextSlot name="eyebrow" as="p" className="eyebrow" />
        <TextSlot name="heading" as="h2" />
      </div>
    );
  },
};

export const richtextBlock: BlockDef = {
  type: 'richtext',
  name: 'Rich text',
  description: 'Formatted text — bold, links, multiple paragraphs.',
  category: 'text',
  icon: 'pilcrow',
  propsSchema: z.object({ html: z.string() }),
  propFields: [{ kind: 'richtext', name: 'html', label: 'Content', default: '<p>Rich text</p>' }],
  styleFields: ['color', 'fontSize', 'mt', 'mb'],
  defaults: { props: { html: '<p>Rich text</p>' } },
  Component: function Richtext({ attrs, style }) {
    return (
      <div {...attrs} style={style}>
        <RichSlot name="html" />
      </div>
    );
  },
};
